<template lang="pug">
    .play-box.memory-game-container(:class="{'play-box_with-warning': isNeedRotate}")
        .warning(v-show='isNeedRotate')
            svg#rotate(version='1.1',
                xmlns='http://www.w3.org/2000/svg',
                x='0px',
                y='0px',
                width='40px',
                height='40px',
                viewBox='0 0 612 612',
                style='enable-background:new 0 0 612 612;',
                xml:space='preserve'
                )
                rotation-icon
            div
                | {{ $i18n.t('map.map_mobile_rotateText') }}
        .play-box__game(v-show='!isLoading && !isNeedRotate')
            game-header(
                @soundStateChanged="headerKey = !headerKey",
                :key="headerKey",
                :slug="type",
                :game-type='parameters && parameters.courseType',
                :is-homework="!!exerciseId"
            )
            .memory-game-container__interactive-place(v-if='!prepareForGame')
                .table-container(v-if='!gameFinished')
                    .memory-table
                        memo-table(
                            :game-items='items',
                            :amount-of-columns='parameters.difficult.amountOfColumns',
                            @selectItem='selectItem'
                        )
                .game-info__container(v-if="!gameFinished")
                    .game-info__block
                        game-info(
                            :score="{left: answers.good, right: answers.bad}",
                            :time="getTime",
                            :is-olympiad="isOlympiad"
                        )
                .memory-game-container__result-container(v-if='gameFinished && !isOlympiad')
                    result-image.memory-game-container__result-image(status="success")
                    result-buttons(
                        :exercise-id="exerciseId"
                        games-type="none"
                        show-restart-button
                        @restartGame="restartGame"
                    )
                        slot
        cs-loader(v-if='isLoading && !isVerticalScreen')
</template>

<script>
    import { shuffle, chunk, cloneDeep } from 'lodash';
    import MemoGameItemsApi from '../../../../api/games/memoGameApi.js';
    import MemoTable from './MemoTable.vue';
    import playBoxGameMixin from '../../../../utils/trainer/playBoxGameMixin.js';
    import olympiadAnswerTimerMixin from '../../../../utils/olympiads/olympiadAnswerTimerMixin.js';

    const DEFAULT_VOLUME = 0.6;

    function DEFAULT_DATA() {
        return {
            items: [],
            tableCells: [],
            answers: {
                good: 0,
                bad: 0,
            },
            selectedItems: [],
            guessedItemsIds: [],
            prepareForGame: true,
            startWidth: window.innerWidth,
            amountOfColumns: null,
            isFetched: false,
            savedTime: 0,
        };
    }

    export default {
        mixins: [playBoxGameMixin, olympiadAnswerTimerMixin],
        data() {
            return DEFAULT_DATA();
        },
        watch: {
            isNeedRotate(val) {
                if (val) {
                    this.savedTime = this.time;
                } else {
                    !this.timer && this.setTimer();
                    this.time = this.savedTime;
                    !this.tableCells.length && this.setTableCells();
                }
            },
        },
        computed: {
            isNeedRotate() {
                return this.isVerticalScreen && this.amountOfColumns >= 5 && this.startWidth < 768;
            },
        },
        methods: {
            async fetchData() {
                try {
                    await this.getExercise();
                    await this.getExample();
                    this.beforeStart();
                } catch (e) {
                    this.isLoading = false;
                    this.$toasted.error(this.$root.$t('errors.getting_level'));
                    this.$router.push({ name: 'dashboard' });
                }
            },
            getExample() {
                let paramsForRequest = {
                    amountOfItems:
                        (this.parameters.difficult.amountOfRows * this.parameters.difficult.amountOfColumns) / 2,
                    type: this.parameters.viewType,
                    groups: this.parameters.selectedItems,
                };
                return MemoGameItemsApi.getItemsByRequest(paramsForRequest)
                    .then(items => {
                        this.setData(items);
                    })
                    .then(() => {
                        this.registerSounds();
                    })
                    .catch(err => this.showErrorMessage(err));
            },
            setData(items) {
                if (this.parameters.viewType === 'image') {
                    items.push(...items);
                }
                this.amountOfColumns = this.parameters.difficult.amountOfColumns;
                items = shuffle(items);
                this.items = shuffle(chunk(items, this.parameters.difficult.amountOfColumns));
                this.isLoading = false;
                this.gameStarted = true;
            },
            beforeStart() {
                this.prepareForGame = false;
                !this.isNeedRotate && !this.timer && this.setTimer();
                this.startGame();
            },
            refreshGameStatBeforeSend() {
                this.gameStat.chainOfAttempts = [];
                this.gameStat.attempts = this.answers.bad + this.answers.good;
                this.gameStat.answers = this.answers.good;
                this.gameStat.percent = this.calculatePercent();
                this.gameStat.isCompleted = this.gameFinished;
                this.gameStat.executionTime = this.time;
                this.gameStat.isCorrect = true;
                this.gameStat.parameters = cloneDeep(this.parameters);
            },
            calculatePercent() {
                let total = this.answers.good + this.answers.bad;
                if (total > 0) {
                    return parseInt((this.answers.good / total) * 100);
                }
                return 0;
            },
            startGame() {
                this.setTableCells();
                if (this.answerDuration) {
                    this.startAnswerTimer();
                }
            },
            setTableCells() {
                this.$nextTick(() => {
                    this.tableCells = document.querySelectorAll('.memory-item');
                    this.tableCells = chunk(this.tableCells, this.parameters.difficult.amountOfColumns);
                });
            },
            selectItem(item, row, column) {
                if (this.selectedItems.length < 2) {
                    let element = this.selectedItems.find(element => element.row === row && element.column === column);
                    if (!element) {
                        this.selectedItems.push({ data: item, row: row, column: column });
                        this.tableCells[row][column].classList.add('memory-item_selected');
                        this.tableCells[row][column].childNodes[0].classList.remove('game-item_hidden');
                        this.tableCells[row][column].childNodes[1].classList.add('memory-item__number_hidden');
                    }
                }
                if (this.selectedItems.length === 2) {
                    this.tableCells.forEach(row => {
                        row.forEach(cell => cell.classList.add('memory-item_disabled'));
                    });
                    this.processReply();
                }
            },
            resetSelectedItems() {
                setTimeout(() => {
                    this.selectedItems.forEach(item => {
                        this.tableCells[item.row][item.column].classList.add('shake');
                        setTimeout(() => {
                            this.tableCells[item.row][item.column].classList.remove('shake');
                            if (!this.guessedItemsIds.includes(item.data.id)) {
                                this.tableCells[item.row][item.column].childNodes[0].classList.add('game-item_hidden');
                                this.tableCells[item.row][item.column].childNodes[1].classList.remove(
                                    'memory-item__number_hidden'
                                );
                                this.tableCells[item.row][item.column].classList.remove('memory-item_selected');
                            }
                        }, 600);
                    });
                    this.selectedItems = [];
                    this.tableCells.forEach(row => {
                        row.forEach(cell => cell.classList.remove('memory-item_disabled'));
                    });
                }, 550);
            },
            processReply() {
                this.stopAnswerTimer();
                switch (this.parameters.viewType) {
                    case 'image':
                        this.processImageReply();
                        break;
                }
            },
            processImageReply() {
                const imageItems = this.selectedItems;
                const isCorrect = imageItems[0].data.id === imageItems[1].data.id;

                if (imageItems.length === 2) {
                    isCorrect ? this.handleCorrectReply() : this.handleIncorrectReply();
                } else {
                    this.handleIncorrectReply();
                }

                if (!this.$offline && (this.user.type === 'student' || this.user.type === 'olympiads-student')) {
                    this.sendStat();
                }
                if (
                    this.answers.good ===
                    (this.parameters.difficult.amountOfRows * this.parameters.difficult.amountOfColumns) / 2
                ) {
                    this.memoryDone();
                }
            },
            handleCorrectReply() {
                this.selectedItems.forEach(item => {
                    this.guessedItemsIds.push(item.data.id);
                    this.tableCells[item.row][item.column].classList.add('memory-item_correct');
                });
                this.$createjs.Sound.play('clickCorrect');
                this.answers.good += 1;
                this.selectedItems = [];
                this.tableCells.forEach(row => {
                    row.forEach(cell => cell.classList.remove('memory-item_disabled'));
                });
            },
            handleIncorrectReply() {
                this.$createjs.Sound.play('clickIncorrect');
                this.answers.bad += 1;
                this.resetSelectedItems();
            },
            memoryDone() {
                if (!this.isOlympiad) {
                    this.soundInstance = this.$createjs.Sound.play('gameDone');
                    this.soundInstance.volume = DEFAULT_VOLUME;
                }
                this.gameFinished = true;
                this.stopTimer();
            },
            beforeGameRestart() {
                Object.assign(this, DEFAULT_DATA());
            },
        },
        components: {
            MemoTable,
        },
    };
</script>

<style lang="scss" scoped>
    @import '@/assets/sass/games/playbox.scss';

    .play-box_with-warning {
        display: table;
    }

    .warning {
        display: table-cell;
        vertical-align: middle;
        text-align: center;
    }

    .play-box__game {
        display: flex;
        flex: 1 1 auto;
    }

    .table-container {
        padding: 0;
        display: flex;
        justify-content: center;
        width: 100%;
        @include media($xlg) {
            padding: 0 10px 0 10px;
        }
    }
    .buttons {
        margin-top: 30px;
        text-align: center;
    }
    .memory-game-container {
        width: 100%;
        min-height: 100%;
        display: flex;
        justify-content: space-around;
        overflow: hidden;
        &__prepare-image {
            display: flex;
            flex-direction: column;
            flex-grow: 1;
            flex-shrink: 1;
            align-items: center;
        }
        &__result-container {
            display: flex;
            flex-direction: column;
            align-items: center;
        }
        &__result-image {
            @include media($sm) {
                max-width: 450px;
                max-height: 450px;
            }
            max-height: 360px;
            max-width: 360px;
            vertical-align: middle;
        }
        &__interactive-place {
            width: 100%;
            height: 100%;
            display: flex;
            flex-direction: column-reverse;
            justify-content: flex-end;
            overflow: hidden;
            @include media($lg) {
                justify-content: center;
                flex-direction: row;
            }
        }
    }
    .warning {
        display: table-cell;
        vertical-align: middle;
        text-align: center;
    }
    .router-link {
        margin: 5px;
    }
    .game-info__block {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
    }
    .buttons {
        display: flex;
    }
    .trainer-button {
        margin-left: 20px;
    }

    .play-box__buttons {
        display: flex;
        flex-direction: column;
        margin-top: 20px;
        @include media($sm) {
            margin-top: 25px;
        }
        @include media($lg) {
            flex-direction: row;
        }
    }

    .play-box__buttons--game {
        margin: 30px 0 0;
        @include media($xlg) {
            margin: 0 0 0 30px;
        }
    }

    .play-box__button {
        margin: 20px 0;
        @include media($lg) {
            margin: 0 20px;
        }
    }

    .play-box__button:first-child {
        margin-top: 0;
        @include media($lg) {
            margin-left: 0;
        }
    }

    .play-box__button:last-child {
        margin-bottom: 0;
        @include media($lg) {
            margin-right: 0;
        }
    }
</style>
